// Door
// Features:
// - Animated door mesh with kinematic physics body
// - Handles door open/close state from store
// - Manages transition to outside (IntroTravel phase) on interaction
// - Plays open/close sound effects

import { useEffect, useRef } from 'react'
import { RigidBody } from '@react-three/rapier'
import * as THREE from 'three'
import gsap from 'gsap'

import { useStore } from '../../store/useStore'
import { useActions, DOOR_STATE } from '../../store/useActions'
import { usePhase, PHASES } from '../../store/usePhase'
import { OVERLAY_FADE_DURATION_MS } from '../../utils/constants'

export default function Door({ nodes, material }) {
    const bodyRef = useRef()
    const angleRef = useRef(0)
    const openAudioRef = useRef(null)
    const closeAudioRef = useRef(null)
    const transitioningRef = useRef(false)

    const doorState = useActions((s) => s.doorState)
    const isSoundOn = useStore((s) => s.isSoundOn)
    const setPhase = usePhase((s) => s.setPhase)
    const showOverlay = useActions((a) => a.showOverlay)

    // Initialization: Audio
    useEffect(() => {
        const aOpen = new Audio('/sounds/door_open.mp3')
        aOpen.preload = 'auto'
        aOpen.volume = isSoundOn ? 0.2 : 0
        openAudioRef.current = aOpen

        const aClose = new Audio('/sounds/door_close.mp3')
        aClose.preload = 'auto'
        aClose.volume = isSoundOn ? 0.2 : 0
        closeAudioRef.current = aClose

        return () => {
            ;[openAudioRef.current, closeAudioRef.current].forEach((audio) => {
                if (audio) {
                    audio.pause()
                    audio = null
                }
            })
        }
    }, [])

    // Audio Volume Update
    useEffect(() => {
        const vol = isSoundOn ? 0.2 : 0
        if (openAudioRef.current) openAudioRef.current.volume = vol
        if (closeAudioRef.current) closeAudioRef.current.volume = vol
    }, [isSoundOn])

    // Animation: Respond to doorState
    useEffect(() => {
        if (!bodyRef.current) return

        const isOpening = doorState === DOOR_STATE.opened
        const endAngle = isOpening ? Math.PI / 2 : 0
        const currentAngle = angleRef.current

        if (Math.abs(currentAngle - endAngle) < 1e-4) return

        // Play Sound
        const sound = isOpening ? openAudioRef.current : closeAudioRef.current
        if (sound) {
            sound.currentTime = 0
            sound.play().catch(() => {})
        }

        // Animate
        const anim = { value: currentAngle }
        gsap.to(anim, {
            value: endAngle,
            duration: 0.8,
            ease: 'power1.inOut',
            overwrite: 'auto',
            onUpdate: () => {
                angleRef.current = anim.value
                const q = new THREE.Quaternion().setFromEuler(
                    new THREE.Euler(0, angleRef.current, 0)
                )
                bodyRef.current.setNextKinematicRotation({
                    x: q.x,
                    y: q.y,
                    z: q.z,
                    w: q.w,
                })
            },
        })
    }, [doorState])

    // Interaction: Click to exit (Transition to IntroTravel)
    useEffect(() => {
        const onMouseDown = (e) => {
            if (e.button !== 0) return
            const state = useStore.getState()

            if (
                !state.isPointerLocked ||
                state.currentHitName !== 'Door' ||
                transitioningRef.current
            )
                return

            transitioningRef.current = true

            // 1. Immediate UI/Control cleanup
            document.exitPointerLock?.()
            state.setCharacterControlsEnabled?.(false)
            state.setUiOn?.(false)
            state.setPointerLocked?.(false)
            state.setCrosshairOn?.(false)
            state.setCurrentHitName?.(null)
            state.setCurrentOutsideHitName?.(null)

            // 2. Start visual transition
            showOverlay()

            // 3. Switch phase after fade
            setTimeout(() => {
                // Ensure clean state for next phase
                document.exitPointerLock?.()
                useStore.getState().setPointerLocked?.(false)
                useStore.getState().setUiOn?.(true)

                setPhase(PHASES.introTravel)
                transitioningRef.current = false
            }, OVERLAY_FADE_DURATION_MS)
        }

        window.addEventListener('mousedown', onMouseDown)
        return () => window.removeEventListener('mousedown', onMouseDown)
    }, [setPhase, showOverlay])

    return (
        <group>
            <RigidBody
                colliders="cuboid"
                type="kinematicPosition"
                userData={{ name: 'Door' }}
                position={[2.983, 1.103, 1.68]}
                ref={bodyRef}
            >
                <mesh geometry={nodes.room_door.geometry} material={material} />
            </RigidBody>
        </group>
    )
}
